import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import Box from '@mui/material/Box';
import {Chip, Grid, Stack, Typography} from '@mui/material';
import { useStateContext } from '../../../hooks/useStateContext';
import api from '../../../services/api';
import {GroupSizeField} from "../components/fields/GroupSizeField";
import {debounce} from "lodash";
import Avatar from "@mui/material/Avatar";
import {EuroRounded} from "@mui/icons-material";
import {ActivityField} from "../components/fields/ActivityField";
import {ActivityPriceTable} from "../components/table/ActivityPriceTable";
import Joyride, { STATUS, Step } from "react-joyride";
import { IconButton } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

// --- CONFIG ---
const LS_KEY = "guideTraining.completed";

const steps: Step[] = [
  {
    target: "body",
    placement: "center",
    title: "Welcome to the training!",
    content: (
      <Box>
        <Typography variant="body1" gutterBottom>
          We’ll guide you through the most important elements. Use the <strong>Next</strong> button to navigate, or close the guide anytime.
        </Typography>
      </Box>
    ),
    disableBeacon: true,
  },
  {
    target: "#section-prices",
    title: "Group size",
    content: "Here you can set the number of people in the group to check the activity prices",
    placement: "bottom",
  },
  {
    target: "#current-eur-exchange",
    title: "Current EUR exchange rate",
    content: "Here you can check the current exchange rate for EUR",
    placement: "bottom",
  },
  {
    target: "#choose-activity",
    title: "Activity",
    content: "Here you can choose which activity’s prices you want to view",
    placement: "top",
  },
  {
    target: "#btn-training-restart",
    title: "Restart training",
    content: "If you want to view the walkthrough again later, click here.",
    placement: "left",
  },
];
interface Props {
}

export const ActivityPricePage: React.ComponentType<Props> = ({  }) => {
  const [rows, setRows] = useState<any>([]);
  const [loading, setLoading] = useState<any>(false);
  const [selectedRow, setSelectedRow] = useState<any>(false);
  const [size, setSize] = useState<any>(10);
  const [eurValue, setEurValue] = useState<any>(0);

  const debouncedOnChange = debounce(handleDebouncedOnChange, 200);

  function handleDebouncedOnChange(value: any) {
    setSize(value);
  }
  
  const fetchPrices = useCallback(async () => {
    setLoading(true);
    await api
      .get(`/guide/activity-prices`)
      .then((result: any) => {
        setRows(result?.rows);
        setEurValue(result?.currency);
        setLoading(false);
      })
      .catch((error) => {
      });
  }, []);

  useEffect(() => {
    fetchPrices();
  }, []);

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Indítsd el automatikusan első látogatáskor
  useEffect(() => {
    const completed = localStorage.getItem(LS_KEY) === "true";
    if (!completed) {
      setRun(true);
    }
  }, []);

  const handleJoyride = (data: any) => {
    const { status, index, type, action } = data;

    // Ha vége vagy kilépés, mentjük, hogy teljesítve
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      localStorage.setItem(LS_KEY, 'true');
      setRun(false);
      setStepIndex(0);
      return;
    }

    // Lépésváltás logika
    if (type === 'step:after') {
      if (action === 'next' || action === 'start') {
        // Ha az utolsó lépésen van, zárjuk le
        if (index + 1 >= steps.length) {
          localStorage.setItem(LS_KEY, 'true');
          setRun(false);
          setStepIndex(0);
        } else {
          setStepIndex(index + 1);
        }
      } else if (action === 'prev') {
        setStepIndex(Math.max(index - 1, 0));
      }
    }

    if (type === 'target:notFound') {
      setStepIndex(action === 'prev' ? Math.max(index - 1, 0)
        : Math.min(index + 1, steps.length - 1));
    }
  };

  const startTraining = () => {
    setStepIndex(0);
    setRun(true);
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          overflow: 'auto',
          padding: '0px'
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack
              sx={{
                padding: '10px'
              }}
              gap={1}
            >
              <Stack>
                <Stack
                  id={"section-prices"}
                  direction={'row'} justifyContent={'flex-end'}
                  sx={{
                    marginTop: '20px'
                  }}
                >
                  <Typography
                    color={'#666'}
                    sx={{
                      width: '170px',
                      lineHeight: '13px',
                    }}
                  >
                    Activity Prices
                    <br/>
                    <small>
                      ({size} person)
                    </small>
                  </Typography>
                  <Box sx={{
                    width: '100%'
                  }}>
                    <GroupSizeField onChange={(value)=>{
                      debouncedOnChange(value);
                    }} />
                  </Box>
                </Stack>
                <Stack direction={'row'} justifyContent={'flex-end'} gap={1}>
                  <Chip
                    id={"current-eur-exchange"}
                    size={'small'} avatar={<Avatar><EuroRounded sx={{
                    fontSize: '12px',
                    zIndex: 0
                  }} /></Avatar>} label={`EUR: ${eurValue}`} />
                </Stack>
              </Stack>

              <Stack
                id={"choose-activity"}
                direction={'row'} justifyContent={'flex-end'} gap={1}>
                <ActivityField onChange={(value)=>{
                  setSelectedRow(value);
                }} rows={rows} />
              </Stack>

              {selectedRow && (
                <ActivityPriceTable row={selectedRow} size={size} eurValue={eurValue} />
              )}
            </Stack>

            <Box>

            </Box>
          </Grid>
        </Grid>
      </Box>


      {/* Joyride */}
      <Joyride
        run={run}
        steps={steps}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        disableScrolling={false}
        scrollToFirstStep
        styles={{
          options: {
            zIndex: 2000,
          },
          tooltip: {
            borderRadius: '10px',
          },
          buttonNext: {
            background: '#256861'
          },
          buttonBack: { opacity: 0.75 },
        }}
        callback={handleJoyride}
        locale={{
          back: 'Back',
          last: 'Done',
          next: 'Next',
          skip: 'Skip',
          close: 'Close',
        }}
      />

      <IconButton
        sx={{
          background: '#256861',
          boxShadow: '0px 1px 5px 1px #00000054',
          position: 'absolute',
          bottom: 16,
          right: 16,
          '&:hover': {
            background: '#256861',
          },
      }}
        id="btn-training-restart"
        onClick={startTraining}
        aria-label="Tréning újraindítása">
        <HelpOutlineIcon sx={{
          color: '#fff'
        }} />
      </IconButton>
    </>
  );
};

function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}